// MarketTrackBoard — primary v4 board: durable Market Tracks (CORE + WATCHLIST
// by default) with curation role, conviction, lifecycle, persistence, latest
// weekly framing, evidence breadth and last-active week. Supporting patterns
// and weekly movements are surfaced as nested rows beneath their parent
// signal — expandable via a per-row caret or the global "Expand all" toggle.

import React, { useMemo, useState } from "react";
import { lifecycleTone, marketZoneForTrack, primaryRoleChipForTrack, visibilityTone } from "../../lib/tracks";
import { groupByParent } from "../../lib/workbench/labels";

const Badge = ({ tone = "muted", children, title }) => (
    <span title={title} className={`badge ${tone === "accent" ? "badge-accent" : tone === "positive" ? "badge-positive" : tone === "warning" ? "badge-warning" : tone === "negative" ? "badge-negative" : tone === "regulatory" ? "badge-regulatory" : tone === "ink" ? "badge-ink" : ""}`}>
        {children}
    </span>
);

/** Track-level unique company breadth across the full track history.
 *  Source priority:
 *    1. track.trackLevelCounts.companies.uniqueBreadth   (lifetime, governed)
 *    2. track.TrackUniqueCompanyBreadth                  (legacy alias)
 *    3. track.LatestCompanyBreadth                       (latest-week fallback)
 *  Returns null when no source is available, so callers can render "—". */
const trackCompanyBreadth = (t) => {
    const lifetime = t?.trackLevelCounts?.companies?.uniqueBreadth;
    if (typeof lifetime === "number" && Number.isFinite(lifetime) && lifetime > 0) return lifetime;
    if (typeof t?.TrackUniqueCompanyBreadth === "number" && Number.isFinite(t.TrackUniqueCompanyBreadth)) return t.TrackUniqueCompanyBreadth;
    if (typeof t?.LatestCompanyBreadth === "number" && Number.isFinite(t.LatestCompanyBreadth)) return t.LatestCompanyBreadth;
    return null;
};

const COMPANIES_COL_TOOLTIP = "Unique companies observed across the full track history.";
const SOURCES_COL_TOOLTIP = "Total distinct sources cited across the full track history.";
const PERSISTENCE_COL_TOOLTIP = "Share of weeks this track was active or on watchlist across its full history.";
const FRAMING_COL_TOOLTIP = "Editorial framing and visibility from the latest indexed week.";
const LATEST_COL_TOOLTIP = "Track score in the latest indexed week.";
const TREND_COL_TOOLTIP = "Change in track score over the last 4 weeks.";
const LAST_SEEN_COL_TOOLTIP = "Most recent week this track was observed in the evidence.";

/** Small caret button rendered in the left-most cell of a parent row when
 *  the parent has at least one supporting pattern or weekly movement. */
const ExpandCaret = ({ expanded, onClick, count, testId }) => (
    <button
        type="button"
        data-testid={testId}
        aria-label={expanded ? "Collapse supporting signals" : "Expand supporting signals"}
        aria-expanded={!!expanded}
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        className="inline-flex items-center justify-center w-5 h-5 rounded text-[10px] text-[hsl(var(--ink-soft))] hover:bg-[hsl(var(--surface-sunken))] transition-transform"
        title={expanded ? "Collapse supporting signals" : `Expand ${count} supporting signal${count === 1 ? "" : "s"}`}
    >
        <span style={{ display: "inline-block", transform: expanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s ease" }}>▶</span>
    </button>
);

/** Compact "2 supporting · 1 movement" hint shown next to the parent
 *  name when collapsed children exist. */
const ChildrenHint = ({ kids }) => {
    const sub = kids.filter((c) => c?._governed?.isSubPattern || c?._role === "SUB_PATTERN").length;
    const weekly = kids.filter((c) => c?._governed?.isWeeklyDynamic).length;
    if (!sub && !weekly) return null;
    const parts = [];
    if (sub) parts.push(`${sub} supporting`);
    if (weekly) parts.push(`${weekly} movement${weekly === 1 ? "" : "s"}`);
    return <span className="text-[10px] text-[hsl(var(--mute))] num">{parts.join(" · ")}</span>;
};

/** External-facing role label for a child row. */
const childRoleChip = (track) => {
    const g = track?._governed;
    if (g?.isWeeklyDynamic) return { label: "This week's movement", tone: "accent" };
    if (g?.isSubPattern || track?._role === "SUB_PATTERN") return { label: "Supporting pattern", tone: "muted" };
    const chip = primaryRoleChipForTrack(track);
    return chip || { label: "Supporting pattern", tone: "muted" };
};

const TrendArrow = ({ value }) => {
    if (value == null) return <span className="text-[hsl(var(--mute))] text-xs">·</span>;
    if (value > 0) return <span className="text-[hsl(var(--positive))] text-xs num">▲ +{value}</span>;
    if (value < 0) return <span className="text-[hsl(var(--negative))] text-xs num">▼ {value}</span>;
    return <span className="text-[hsl(var(--mute))] text-xs">– 0</span>;
};

const PersistenceCell = ({ track, totalWeeks }) => {
    const observed = track.WeeksObserved || (track.ObservedYearWeeksJSON || []).length || 0;
    const ratio = track.PersistenceRatio != null ? track.PersistenceRatio : (totalWeeks ? observed / totalWeeks : 0);
    const sw = track.SignalWeeks || 0;
    const wl = track.WatchlistWeeks || 0;
    return (
        <div className="text-xs num leading-tight">
            <div className="font-medium">{observed}{totalWeeks ? `/${totalWeeks}` : ""} weeks</div>
            <div className="text-[10px] text-[hsl(var(--mute))]">{Math.round((ratio || 0) * 100)}% · {sw} signal · {wl} watch</div>
        </div>
    );
};

/** Does this single track match the free-text search? */
const trackMatchesSearch = (t, q) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return (
        (t.StableTrackName || "").toLowerCase().includes(needle) ||
        (t.SuggestedDisplayLabel || "").toLowerCase().includes(needle) ||
        (t.MacroTheme || "").toLowerCase().includes(needle) ||
        (t.PrimaryUseCase || "").toLowerCase().includes(needle) ||
        (t.LatestWeeklyFraming || "").toLowerCase().includes(needle) ||
        (t.RepresentativeCompaniesJSON || []).some((c) => (c || "").toLowerCase().includes(needle))
    );
};

/** Does this single track match the active role filter? Mirrors the
 *  pre-hierarchy logic so the role dropdown still works during the
 *  expandable transition. */
const trackMatchesRole = (t, roleFilter) => {
    if (!roleFilter || roleFilter === "ALL") return true;
    if (roleFilter === "CORE_TRACK") return t._role === "CORE_TRACK";
    if (roleFilter === "WATCHLIST_TRACK") return t._role === "WATCHLIST_TRACK";
    if (roleFilter === "SUB_PATTERN") return (t._governed && t._governed.isSubPattern) || t._role === "SUB_PATTERN";
    if (roleFilter === "WEEKLY_DYNAMIC") return t._governed && t._governed.isWeeklyDynamic;
    return (t._role || "") === roleFilter;
};

const MarketTrackBoard = ({
    tracks,
    allTracks,
    selectedId,
    onSelect,
    totalWeeks,
    showExploratory,
    onToggleExploratory,
    summary,
    search,
    onSearch,
    roleFilter,
    onRoleFilter,
    macroThemeFilter,
    onClearMacroTheme,
}) => {
    const [sortKey, setSortKey] = useState("default");
    const [sortDir, setSortDir] = useState("desc");
    // Per-parent expansion state. The "Expand all supporting signals"
    // toggle (formerly "Show supporting signals") flips this for every
    // parent at once via the `showExploratory` prop; individual carets
    // toggle a single parent. Search hits on a child auto-expand its
    // parent without writing to this state, so collapsing afterwards
    // returns to the user's last manual choice.
    const [expandedMap, setExpandedMap] = useState({});
    const toggleExpand = (id) => setExpandedMap((m) => ({ ...m, [id]: !m[id] }));

    const toggleSort = (k) => {
        if (sortKey === k) setSortDir(sortDir === "desc" ? "asc" : "desc");
        else { setSortKey(k); setSortDir("desc"); }
    };

    // Build the parent → children map from the FULL governed dataset so
    // children resolve correctly even when the visible `tracks` slice is
    // filtered (e.g. by market-zone chip). Falls back to the visible
    // `tracks` list when the parent does not supply `allTracks`.
    const groups = useMemo(() => {
        const src = (allTracks && allTracks.length) ? allTracks : (tracks || []);
        return groupByParent(src);
    }, [allTracks, tracks]);

    // Map every TrackID to its parent ID (or itself if it IS a parent).
    // Used to lift a search-matching child back to its visible parent row.
    const parentOf = useMemo(() => {
        const m = new Map();
        for (const g of groups) {
            if (!g.parent) continue;
            for (const c of g.children) m.set(c.TrackID, g.parent.TrackID);
        }
        return m;
    }, [groups]);

    // Set of parent IDs that have at least one descendant matching the
    // active search OR role filter (when those filters target a child
    // role). When non-empty, those parents render auto-expanded.
    const autoExpandedParents = useMemo(() => {
        const set = new Set();
        const q = (search || "").trim();
        const filteringForChildRole = roleFilter === "SUB_PATTERN" || roleFilter === "WEEKLY_DYNAMIC";
        if (!q && !filteringForChildRole) return set;
        for (const g of groups) {
            if (!g.parent) continue;
            for (const c of g.children) {
                if (c.TrackID === g.parent.TrackID) continue;
                const matchesText = q ? trackMatchesSearch(c, q) : true;
                const matchesRole = filteringForChildRole ? trackMatchesRole(c, roleFilter) : true;
                if (matchesText && matchesRole) {
                    set.add(g.parent.TrackID);
                    break;
                }
            }
        }
        return set;
    }, [groups, search, roleFilter]);

    // Visible PARENTS — top-level signals only, filtered by search /
    // role / macro chip. A parent is visible if (a) it itself matches
    // both filters, OR (b) one of its descendants matches (so the user
    // can still see the matching supporting/weekly row under its parent).
    const parents = useMemo(() => {
        const q = (search || "").trim();
        const out = [];
        const filteringForChildRole = roleFilter === "SUB_PATTERN" || roleFilter === "WEEKLY_DYNAMIC";
        // Pre-build the set of visible TrackIDs from the parent's
        // `tracks` prop so the board still respects MarketView-level
        // filters (e.g. `macroThemeFilter`, which is applied upstream).
        // Falls back to "no restriction" when the prop is empty.
        const tracksSet = (tracks && tracks.length) ? new Set(tracks.map((t) => t.TrackID)) : null;
        for (const g of groups) {
            if (!g.parent) continue; // skip the "__orphans__" bucket
            const parent = g.parent;
            // Respect upstream filtering (e.g. macroThemeFilter): when
            // the parent isn't in the visible-set, don't render it.
            if (tracksSet && !tracksSet.has(parent.TrackID)) continue;
            const parentMatchesText = q ? trackMatchesSearch(parent, q) : true;
            const parentMatchesRole = filteringForChildRole ? false : trackMatchesRole(parent, roleFilter);
            const childMatch = autoExpandedParents.has(parent.TrackID);
            if (!parentMatchesText && !childMatch) continue;
            if (!parentMatchesRole && !childMatch) continue;
            out.push(parent);
        }
        if (sortKey === "default") return out;
        const getVal = (t) => {
            switch (sortKey) {
                case "name": return (t.StableTrackName || "").toLowerCase();
                case "persistence": return t.PersistenceRatio ?? 0;
                case "score": return t.LatestScore ?? 0;
                case "avg": return t.AvgScore ?? 0;
                case "sources": return t.TotalSources ?? 0;
                case "articles": return t.TotalArticles ?? 0;
                case "companies": return trackCompanyBreadth(t) ?? 0;
                case "lastSeen": return t.LastSeenWeekIndex ?? 0;
                case "trend4w": return t.ScoreTrend4w ?? -999;
                case "trend12w": return t.ScoreTrend12w ?? -999;
                default: return 0;
            }
        };
        out.sort((a, b) => {
            const av = getVal(a), bv = getVal(b);
            if (av < bv) return sortDir === "asc" ? -1 : 1;
            if (av > bv) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
        return out;
    }, [groups, tracks, search, roleFilter, sortKey, sortDir, autoExpandedParents]);

    // Children to render for each visible parent — respects expansion
    // state (manual OR auto-from-search) AND the search/role filter so
    // a search like "lending" highlights only the matching child rows.
    const childrenByParentId = useMemo(() => {
        const out = new Map();
        const q = (search || "").trim();
        const filteringForChildRole = roleFilter === "SUB_PATTERN" || roleFilter === "WEEKLY_DYNAMIC";
        for (const g of groups) {
            if (!g.parent) continue;
            const expanded = showExploratory || expandedMap[g.parent.TrackID] || autoExpandedParents.has(g.parent.TrackID);
            if (!expanded) { out.set(g.parent.TrackID, []); continue; }
            const kids = g.children.filter((c) => c.TrackID !== g.parent.TrackID).filter((c) => {
                // When the user is filtering for child-roles, only show
                // children matching the role (the parent is still shown
                // because at least one child matched). When searching,
                // only show children matching the text query.
                if (filteringForChildRole && !trackMatchesRole(c, roleFilter)) return false;
                if (q && !trackMatchesSearch(c, q)) return false;
                return true;
            });
            out.set(g.parent.TrackID, kids);
        }
        return out;
    }, [groups, expandedMap, showExploratory, search, roleFilter, autoExpandedParents]);

    // Total VISIBLE row count for the section header.
    const visibleRowCount = useMemo(() => {
        let n = parents.length;
        for (const p of parents) n += (childrenByParentId.get(p.TrackID) || []).length;
        return n;
    }, [parents, childrenByParentId]);

    const SortHead = ({ k, children, align = "left", title }) => (
        <th title={title} className={`px-3 py-2 text-[11px] font-medium text-[hsl(var(--mute))] uppercase tracking-wider ${align === "right" ? "text-right" : "text-left"}`}>
            <button onClick={() => toggleSort(k)} className="inline-flex items-center gap-1 hover:text-[hsl(var(--ink))]">
                {children}
                {sortKey === k && <span className="text-[9px]">{sortDir === "desc" ? "▼" : "▲"}</span>}
            </button>
        </th>
    );

    const otherCount = (summary?.subPatterns || 0) + (summary?.uncurated || 0) + (summary?.exploratoryClusters || 0);
    // Governed summary counts. Sourced from the FULL track list
    // (`allTracks`, when provided) so counts reflect the whole governed
    // dataset rather than the currently visible board slice. Falls back to
    // the visible `tracks` list when the parent does not supply `allTracks`.
    const governedCounts = useMemo(() => {
        const src = (allTracks && allTracks.length) ? allTracks : (tracks || []);
        let topLevel = 0; let subPatterns = 0; let weekly = 0; let governed = 0;
        for (const t of src) {
            if (!t) continue;
            const g = t._governed;
            if (!g) continue;
            governed += 1;
            if (g.isTopLevel) topLevel += 1;
            else if (g.isSubPattern) subPatterns += 1;
            else if (g.isWeeklyDynamic) weekly += 1;
        }
        return { topLevel, subPatterns, weekly, hasGoverned: governed > 0 };
    }, [allTracks, tracks]);

    return (
        <section className="card-flat" data-testid="market-track-board">
            <header className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-[hsl(var(--line))]">
                <div>
                    <div className="eyebrow">Market signals</div>
                    <h2 className="display text-xl mt-1">{(() => {
                        const r = (roleFilter || "ALL").toUpperCase();
                        if (r === "CORE_TRACK") return `Core signals · ${parents.length}`;
                        if (r === "WATCHLIST_TRACK") return `Watchlist signals · ${parents.length}`;
                        if (r === "SUB_PATTERN") return `Supporting patterns · ${visibleRowCount - parents.length}`;
                        if (r === "WEEKLY_DYNAMIC") return `This week's movements · ${visibleRowCount - parents.length}`;
                        if (governedCounts.hasGoverned) {
                            return showExploratory
                                ? `All governed market signals · ${visibleRowCount}`
                                : `Top-level signals · ${parents.length}`;
                        }
                        return showExploratory
                            ? `All curated market signals · ${visibleRowCount}`
                            : `Core & watchlist signals · ${parents.length}`;
                    })()}</h2>
                    {summary && (
                        <div className="text-[11px] text-[hsl(var(--mute))] mt-1.5 num" data-testid="board-breakdown">
                            {governedCounts.hasGoverned ? (
                                <>
                                    <span>{governedCounts.topLevel} top-level signals</span>
                                    <span className="mx-1.5">·</span>
                                    <span>{governedCounts.subPatterns} sub-patterns</span>
                                    <span className="mx-1.5">·</span>
                                    <span>{governedCounts.weekly} weekly movements</span>
                                </>
                            ) : (
                                <>
                                    <span>{summary.total} signals</span>
                                    <span className="mx-1.5">·</span>
                                    <span>{summary.coreTracks} core</span>
                                    <span className="mx-1.5">·</span>
                                    <span>{summary.watchlistTracks} watchlist</span>
                                    <span className="mx-1.5">·</span>
                                    <span>{summary.subPatterns} sub-patterns</span>
                                    {summary.uncurated > 0 && (<><span className="mx-1.5">·</span><span>{summary.uncurated} uncurated</span></>)}
                                </>
                            )}
                        </div>
                    )}
                    {macroThemeFilter && (
                        <div className="mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[11px] font-medium border border-[hsl(var(--accent))] bg-[hsl(var(--accent-soft))] text-[hsl(var(--accent-ink))]" data-testid="board-macro-filter-chip">
                            <span aria-hidden>◆</span>
                            <span>Market zone: {macroThemeFilter}</span>
                            {onClearMacroTheme && (
                                <button
                                    type="button"
                                    onClick={onClearMacroTheme}
                                    className="text-[hsl(var(--accent-ink))] hover:underline"
                                    data-testid="board-clear-macro-filter"
                                >
                                    Clear ×
                                </button>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex-1" />
                {onToggleExploratory && otherCount > 0 && (
                    <button
                        type="button"
                        data-testid="toggle-exploratory"
                        onClick={onToggleExploratory}
                        title={governedCounts.hasGoverned
                            ? (showExploratory
                                ? "Collapses every parent signal's supporting patterns and weekly movements."
                                : "Expands every parent signal to reveal its supporting patterns and weekly movements at once.")
                            : "Reveals sub-patterns and early-stage / uncurated signals."}
                        className={`text-[11px] font-medium px-2.5 py-1 rounded border transition-colors num ${showExploratory ? "border-[hsl(var(--accent))] text-[hsl(var(--accent-ink))] bg-[hsl(var(--accent-soft))]" : "border-[hsl(var(--line))] text-[hsl(var(--ink-soft))] hover:border-[hsl(var(--accent))]"}`}
                        aria-pressed={!!showExploratory}
                    >
                        {governedCounts.hasGoverned
                            ? (showExploratory
                                ? "Collapse all supporting signals"
                                : `Expand all supporting signals · ${governedCounts.subPatterns + governedCounts.weekly}`)
                            : (showExploratory ? "Hide early-stage signals" : `Show early-stage / uncurated signals · ${otherCount}`)}
                    </button>
                )}
                <input
                    data-testid="track-search-input"
                    type="text"
                    value={search}
                    onChange={(e) => onSearch(e.target.value)}
                    placeholder="Search signals, themes, companies…"
                    className="w-56 px-3 py-1.5 text-sm bg-[hsl(var(--surface-sunken))] border border-[hsl(var(--line))] rounded focus:border-[hsl(var(--accent))] outline-none"
                />
                <select data-testid="filter-role" value={roleFilter || "ALL"} onChange={(e) => onRoleFilter(e.target.value)} className="px-2 py-1.5 text-sm bg-[hsl(var(--surface-sunken))] border border-[hsl(var(--line))] rounded">
                    <option value="ALL">All roles</option>
                    <option value="CORE_TRACK">Core signal</option>
                    <option value="WATCHLIST_TRACK">Watchlist signal</option>
                    {/* Sub-pattern / Weekly movement options are only
                        offered when supporting signals are included in
                        the visible table, so the dropdown never lands
                        on an empty `Weekly movements · 0` state when
                        the governed summary lists 7 of them. Toggling
                        supporting signals off while one of these is
                        selected is handled by the parent (resets the
                        role filter to `ALL`). */}
                    {(showExploratory || !governedCounts.hasGoverned) && <option value="SUB_PATTERN">Sub-pattern</option>}
                    {governedCounts.hasGoverned && showExploratory && <option value="WEEKLY_DYNAMIC">Weekly movement</option>}
                </select>
            </header>
            <div className="overflow-x-auto max-h-[640px] overflow-y-auto">
                <table className="w-full text-sm">
                    <thead className="bg-[hsl(var(--surface-sunken))] sticky top-0 z-10">
                        <tr className="border-b border-[hsl(var(--line))]">
                            <th className="px-2 py-2 w-6"></th>
                            <SortHead k="name">Signal</SortHead>
                            <th className="px-3 py-2 text-[11px] font-medium text-[hsl(var(--mute))] uppercase tracking-wider text-left">Role</th>
                            <th className="px-3 py-2 text-[11px] font-medium text-[hsl(var(--mute))] uppercase tracking-wider text-left">Conviction</th>
                            <th className="px-3 py-2 text-[11px] font-medium text-[hsl(var(--mute))] uppercase tracking-wider text-left">Lifecycle</th>
                            <SortHead k="persistence" title={PERSISTENCE_COL_TOOLTIP}>Persistence</SortHead>
                            <th title={FRAMING_COL_TOOLTIP} className="px-3 py-2 text-[11px] font-medium text-[hsl(var(--mute))] uppercase tracking-wider text-left max-w-[260px]">Latest weekly framing</th>
                            <SortHead k="score" align="right" title={LATEST_COL_TOOLTIP}>Latest</SortHead>
                            <SortHead k="trend4w" align="right" title={TREND_COL_TOOLTIP}>Δ4w</SortHead>
                            <SortHead k="sources" align="right" title={SOURCES_COL_TOOLTIP}>Sources</SortHead>
                            <SortHead k="companies" align="right" title={COMPANIES_COL_TOOLTIP}>Companies</SortHead>
                            <SortHead k="lastSeen" align="right" title={LAST_SEEN_COL_TOOLTIP}>Last seen</SortHead>
                        </tr>
                    </thead>
                    <tbody>
                        {parents.map((t) => {
                            const isSelected = t.TrackID === selectedId;
                            const kids = childrenByParentId.get(t.TrackID) || [];
                            const hasChildren = (groups.find((g) => g.parent && g.parent.TrackID === t.TrackID)?.children.length || 1) > 1;
                            const isExpanded = showExploratory || expandedMap[t.TrackID] || autoExpandedParents.has(t.TrackID);
                            const totalKids = (groups.find((g) => g.parent && g.parent.TrackID === t.TrackID)?.children.length || 1) - 1;
                            const groupChildren = groups.find((g) => g.parent && g.parent.TrackID === t.TrackID)?.children?.filter((c) => c.TrackID !== t.TrackID) || [];
                            return (
                                <React.Fragment key={t.TrackID}>
                                    <tr
                                        data-testid={`row-track-${t.TrackID}`}
                                        onClick={() => onSelect(t.TrackID)}
                                        className={`border-b border-[hsl(var(--line))] cursor-pointer row-hover ${isSelected ? "bg-[hsl(var(--accent-soft))]" : ""}`}
                                    >
                                        <td className="px-2 py-3 align-top w-6">
                                            {hasChildren && (
                                                <ExpandCaret
                                                    expanded={isExpanded}
                                                    onClick={() => toggleExpand(t.TrackID)}
                                                    count={totalKids}
                                                    testId={`expand-${t.TrackID}`}
                                                />
                                            )}
                                        </td>
                                        <td className="px-3 py-3 align-top max-w-[280px]">
                                            <div className="text-sm font-semibold text-[hsl(var(--ink))] leading-snug">{t.StableTrackName || "—"}</div>
                                            <div className="text-[11px] text-[hsl(var(--mute))] mt-0.5 truncate flex items-center gap-2">
                                                <span className="truncate">{marketZoneForTrack(t) || t.PrimaryUseCase || "—"}</span>
                                                {hasChildren && !isExpanded && <ChildrenHint kids={groupChildren} />}
                                            </div>
                                        </td>
                                        <td className="px-3 py-3 align-top">
                                            {(() => {
                                                const chip = primaryRoleChipForTrack(t);
                                                return chip ? <Badge tone={chip.tone}>{chip.label}</Badge> : <span className="text-[10px] text-[hsl(var(--mute))]">—</span>;
                                            })()}
                                        </td>
                                        <td className="px-3 py-3 align-top text-xs">{t.ConvictionLevel || "—"}</td>
                                        <td className="px-3 py-3 align-top">
                                            <Badge tone={lifecycleTone(t.LifecycleStatus)}>{(t.LifecycleStatus || "—").toLowerCase()}</Badge>
                                        </td>
                                        <td className="px-3 py-3 align-top"><PersistenceCell track={t} totalWeeks={totalWeeks} /></td>
                                        <td className="px-3 py-3 align-top max-w-[300px]">
                                            <div className="text-xs text-[hsl(var(--ink-soft))] leading-snug line-clamp-2">{t.LatestWeeklyFraming || "—"}</div>
                                            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                                                {t.LatestVisibility && <Badge tone={visibilityTone(t.LatestVisibility)}>{t.LatestVisibility}</Badge>}
                                            </div>
                                        </td>
                                        <td className="px-3 py-3 align-top text-right num text-sm font-medium">{t.LatestScore ?? "—"}</td>
                                        <td className="px-3 py-3 align-top text-right"><TrendArrow value={t.ScoreTrend4w} /></td>
                                        <td className="px-3 py-3 align-top text-right num text-xs" title={SOURCES_COL_TOOLTIP}>{t.TotalSources ?? "—"}</td>
                                        <td className="px-3 py-3 align-top text-right num text-xs" title={COMPANIES_COL_TOOLTIP}>{trackCompanyBreadth(t) ?? "—"}</td>
                                        <td className="px-3 py-3 align-top text-right text-xs num text-[hsl(var(--mute))]">{t.LastSeenYearWeek || t.LastSeenWeek || "—"}</td>
                                    </tr>
                                    {/* Indented child rows — supporting patterns and weekly
                                        movements. Click selects the child in the existing
                                        detail panel via the same `onSelect` callback so
                                        defensive fallbacks in the panel continue to apply. */}
                                    {kids.map((c) => {
                                        const chip = childRoleChip(c);
                                        const isChildSelected = c.TrackID === selectedId;
                                        return (
                                            <tr
                                                key={c.TrackID}
                                                data-testid={`row-track-${c.TrackID}`}
                                                onClick={() => onSelect(c.TrackID)}
                                                className={`border-b border-[hsl(var(--line))] cursor-pointer row-hover bg-[hsl(var(--surface-sunken))]/40 ${isChildSelected ? "bg-[hsl(var(--accent-soft))]" : ""}`}
                                            >
                                                <td className="px-2 py-2.5 align-top w-6"></td>
                                                <td className="px-3 py-2.5 align-top max-w-[280px]">
                                                    <div className="pl-4 border-l-2 border-[hsl(var(--line))]">
                                                        <div className="text-[13px] text-[hsl(var(--ink-soft))] leading-snug">{c.StableTrackName || "—"}</div>
                                                        <div className="text-[10px] text-[hsl(var(--mute))] mt-0.5 truncate">{marketZoneForTrack(c) || c.PrimaryUseCase || "—"}</div>
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2.5 align-top"><Badge tone={chip.tone}>{chip.label}</Badge></td>
                                                <td className="px-3 py-2.5 align-top text-[11px] text-[hsl(var(--ink-soft))]">{c.ConvictionLevel || "—"}</td>
                                                <td className="px-3 py-2.5 align-top">
                                                    {c.LifecycleStatus ? <Badge tone={lifecycleTone(c.LifecycleStatus)}>{c.LifecycleStatus.toLowerCase()}</Badge> : <span className="text-[10px] text-[hsl(var(--mute))]">—</span>}
                                                </td>
                                                <td className="px-3 py-2.5 align-top"><PersistenceCell track={c} totalWeeks={totalWeeks} /></td>
                                                <td className="px-3 py-2.5 align-top max-w-[300px]">
                                                    <div className="text-[11px] text-[hsl(var(--ink-soft))] leading-snug line-clamp-2">{c.LatestWeeklyFraming || "—"}</div>
                                                    <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                                                        {c.LatestVisibility && <Badge tone={visibilityTone(c.LatestVisibility)}>{c.LatestVisibility}</Badge>}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2.5 align-top text-right num text-[12px] text-[hsl(var(--ink-soft))]">{c.LatestScore ?? "—"}</td>
                                                <td className="px-3 py-2.5 align-top text-right"><TrendArrow value={c.ScoreTrend4w} /></td>
                                                <td className="px-3 py-2.5 align-top text-right num text-[10px]" title={SOURCES_COL_TOOLTIP}>{c.TotalSources ?? "—"}</td>
                                                <td className="px-3 py-2.5 align-top text-right num text-[10px]" title={COMPANIES_COL_TOOLTIP}>{trackCompanyBreadth(c) ?? "—"}</td>
                                                <td className="px-3 py-2.5 align-top text-right text-[10px] num text-[hsl(var(--mute))]">{c.LastSeenYearWeek || c.LastSeenWeek || "—"}</td>
                                            </tr>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}
                        {parents.length === 0 && (
                            <tr><td colSpan={12} className="px-4 py-8 text-center text-sm text-[hsl(var(--mute))]">No signals match the current filters.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

export default MarketTrackBoard;
